import React, { Component } from 'react';
import { View, TextInput, TouchableWithoutFeedback, Keyboard, Platform, I18nManager, StyleSheet, TextStyle, ViewStyle, KeyboardType } from 'react-native';
import * as Clipboard from 'expo-clipboard';

const styles = StyleSheet.create({
  defaultTextFieldStyle: {
    width: 45,
    height: 45,
    borderColor: 'rgba(226, 226, 226, 1)',
    borderWidth: 1,
    borderRadius: 2,
    textAlign: 'center',
    color: 'rgba(226, 226, 226, 1)',
  },
  otpTouchableStyle: {
    width: '100%',
    height: '100%',
  },
});

export interface InputProps {
  /**
   * Digits of pins in the OTP
   */
  pinCount: number;
  /**
   * Style of the input fields
   */
  codeInputFieldStyle?: TextStyle;
  /**
   * Style of highlighted status for input fields
   */
  codeInputHighlightStyle?: TextStyle;
  /**
   * Callback function
   * Trigger when all fields of the OTP has been filled
   *
   * @param code The verification code
   */
  onCodeFilled?: (code: string) => void;
  /**
   * Callback function
   * Trigger when a field of the OTP is changed
   *
   * @param code The verification code
   */
  onCodeChanged?: (code: string) => void;
  /**
   * If keyboard is automatically brought up when OTP is loaded.
   */
  autoFocusOnLoad?: boolean;
  /**
   * Initial pin code
   */
  code?: string;
  /**
   * Secure input text
   */
  secureTextEntry?: boolean;
  /**
   * Set editable for inputs
   */
  editable?: boolean;
  /**
   * Type of the keyboard
   */
  keyboardType?: KeyboardType;
  /**
   * Placeholder character to fill all inputs when the OTP is empty
   */
  placeholderCharacter?: string;
  /**
   * Placeholder text color of inputs
   */
  placeholderTextColor?: string;
  /**
   * Style of the OTP container view
   */
  style?: ViewStyle;
  /**
   * The highlight (and cursor on iOS) color of the text input.
   */
  selectionColor?: string;
  /**
   * If inputs are automatically cleared.
   */
  clearInputs?: boolean;

  /**
   * Keyboard appearance. The value can be 'default', 'dark' or 'light'.
   */
  keyboardAppearance?: 'default' | 'dark' | 'light';
}
export default class OTPInputView extends Component<InputProps, any> {
  private fields = [];
  private timer;
  private hasCheckedClipBoard;
  private clipBoardCode;
  private keyboardDidHideListener;

  constructor(props: InputProps) {
    super(props);

    const { code } = props;
    this.state = {
      digits: codeToArray(code),
      selectedIndex: props.autoFocusOnLoad ? 0 : -1,
    };
  }

  copyCodeFromClipBoardOnAndroid = () => {
    if (Platform.OS === 'android') {
      this.checkPinCodeFromClipBoard();
      this.timer = setInterval(this.checkPinCodeFromClipBoard, 400);
    }
  };

  bringUpKeyBoardIfNeeded = () => {
    const { autoFocusOnLoad, pinCount } = this.props;
    const digits = this.getDigits();
    const focusIndex = digits.length ? digits.length - 1 : 0;
    if (focusIndex < pinCount && autoFocusOnLoad) {
      this.focusField(focusIndex);
    }
  };

  getDigits = () => {
    const { digits: innerDigits } = this.state;
    const { code } = this.props;
    return code === undefined ? innerDigits : code.split('');
  };

  handleKeyboardDidHide = () => {
    this.blurAllFields();
  };
  notifyCodeChanged = () => {
    const { digits } = this.state;
    const code = digits.join('');
    const { onCodeChanged } = this.props;
    if (onCodeChanged) {
      onCodeChanged(code);
    }
  };

  checkPinCodeFromClipBoard = () => {
    const { pinCount, onCodeFilled } = this.props;
    const regexp = new RegExp(`^\\d{${pinCount}}$`);
    Clipboard.getStringAsync()
      .then((code) => {
        if (this.hasCheckedClipBoard && regexp.test(code) && this.clipBoardCode !== code) {
          this.setState(
            {
              digits: code.split(''),
            },
            () => {
              this.blurAllFields();
              this.notifyCodeChanged();
              onCodeFilled?.(code);
            },
          );
        }
        this.clipBoardCode = code;
        this.hasCheckedClipBoard = true;
      })
      .catch(() => {});
  };

  handleChangeText = (index, text) => {
    const { onCodeFilled, pinCount } = this.props;
    const digits = this.getDigits();
    let newdigits = digits.slice();
    const oldTextLength = newdigits[index] ? newdigits[index].length : 0;
    const newTextLength = text.length;
    if (newTextLength - oldTextLength === pinCount) {
      // user pasted text in.
      newdigits = text.split('').slice(oldTextLength, newTextLength);
      this.setState({ digits: newdigits }, this.notifyCodeChanged);
    } else {
      if (text.length === 0) {
        if (newdigits.length > 0) {
          newdigits = newdigits.slice(0, newdigits.length - 1);
        }
      } else {
        text.split('').forEach((value) => {
          if (index < pinCount) {
            newdigits[index] = value;
            index += 1;
          }
        });
        index -= 1;
      }
      this.setState({ digits: newdigits }, this.notifyCodeChanged);
    }
    const result = newdigits.join('');
    if (result.length >= pinCount) {
      onCodeFilled?.(result);
      this.focusField(pinCount - 1);
      this.blurAllFields();
    } else if (text.length > 0 && index < pinCount - 1) {
      this.focusField(index + 1);
    }
  };

  handleKeyPressTextInput = (index, key) => {
    const digits = this.getDigits();
    if (key === 'Backspace') {
      if (!digits[index] && index > 0) {
        this.handleChangeText(index - 1, '');
        this.focusField(index - 1);
      }
    }
  };

  blurAllFields = () => {
    this.fields.forEach((field) => field.blur());
    this.setState({
      selectedIndex: -1,
    });
  };

  clearAllFields = (force = false) => {
    const { clearInputs, code } = this.props;
    if ((clearInputs && code === '') || force) {
      this.setState({ digits: [], selectedIndex: 0 });
    }
  };

  renderOneInputField = (_, index) => {
    const { codeInputFieldStyle, codeInputHighlightStyle, secureTextEntry, editable, keyboardType, selectionColor, keyboardAppearance } = this.props;
    const { defaultTextFieldStyle } = styles;
    const { selectedIndex, digits } = this.state;
    const { clearInputs, placeholderCharacter, placeholderTextColor } = this.props;
    // @ts-ignore
    const { color: defaultPlaceholderTextColor } = { ...defaultTextFieldStyle, ...codeInputFieldStyle };
    return (
      <View pointerEvents="none" key={`${index}view`} testID="inputSlotView">
        <TextInput
          testID="textInput"
          underlineColorAndroid="rgba(0,0,0,0)"
          style={selectedIndex === index ? [defaultTextFieldStyle, codeInputFieldStyle, codeInputHighlightStyle] : [defaultTextFieldStyle, codeInputFieldStyle]}
          ref={(ref) => {
            this.fields[index] = ref;
          }}
          onChangeText={(text) => {
            this.handleChangeText(index, text);
          }}
          onKeyPress={({ nativeEvent: { key } }) => {
            this.handleKeyPressTextInput(index, key);
          }}
          value={!clearInputs ? (digits[index] ?? '') : ''}
          keyboardAppearance={keyboardAppearance}
          keyboardType={keyboardType}
          textContentType={isAutoFillSupported ? 'oneTimeCode' : 'none'}
          key={index}
          selectionColor={selectionColor}
          secureTextEntry={secureTextEntry}
          editable={editable}
          placeholder={placeholderCharacter}
          placeholderTextColor={placeholderTextColor || defaultPlaceholderTextColor}
        />
      </View>
    );
  };

  renderTextFields = () => {
    const { pinCount } = this.props;
    const array = new Array(pinCount).fill(0);
    return array.map(this.renderOneInputField);
  };

  focusField = (index) => {
    if (index < this.fields.length) {
      this.fields[index].focus();
      this.setState({
        selectedIndex: index,
      });
    }
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { code } = this.props;
    if (nextProps.code !== code) {
      this.setState({ digits: codeToArray(nextProps.code) });
    }
  }
  componentDidMount() {
    this.copyCodeFromClipBoardOnAndroid();
    this.bringUpKeyBoardIfNeeded();
    this.keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', this.handleKeyboardDidHide);
  }
  componentWillUnmount() {
    let _a;
    if (this.timer) {
      clearInterval(this.timer);
    }
    // @ts-ignore

    (_a = this.keyboardDidHideListener) === null || _a === void 0 ? void 0 : _a.remove();
  }
  render() {
    const { pinCount, style, clearInputs } = this.props;
    const digits = this.getDigits();
    return (
      <View testID="OTPInputView" style={style}>
        <TouchableWithoutFeedback
          style={styles.otpTouchableStyle}
          onPress={() => {
            if (!clearInputs) {
              const filledPinCount = digits.filter((digit) => {
                return digit !== null && digit !== undefined;
              }).length;
              this.focusField(Math.min(filledPinCount, pinCount - 1));
            } else {
              this.clearAllFields();
              this.focusField(0);
            }
          }}
        >
          <View
            style={{ flexDirection: I18nManager.isRTL ? 'row-reverse' : 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%', height: '100%' }}
          >
            {this.renderTextFields()}
          </View>
        </TouchableWithoutFeedback>
      </View>
    );
  }
}

const codeToArray = (code) => {
  let _a;
  // @ts-ignore

  return (_a = code === null || code === void 0 ? void 0 : code.split('')) !== null && _a !== void 0 ? _a : [];
};
const majorVersionIOS = parseInt(String(Platform.Version), 10);
const isAutoFillSupported = Platform.OS === 'ios' && majorVersionIOS >= 12;

// @ts-ignore
OTPInputView.defaultProps = {
  pinCount: 6,
  autoFocusOnLoad: true,
  secureTextEntry: false,
  editable: true,
  keyboardAppearance: 'default',
  keyboardType: 'number-pad',
  clearInputs: false,
  placeholderCharacter: '',
  selectionColor: '#000',
};
